/**
 * DonutChart — pure SVG multi-segment donut.
 * Used for inventory health, payment mix, AR aging.
 *
 * Usage:
 *   <DonutChart
 *     size={120}
 *     segments={[
 *       { label: 'Healthy', value: 60, color: '#16A34A' },
 *       { label: 'Low',     value: 20, color: '#D97706' },
 *       { label: 'Out',     value: 10, color: '#DC2626' },
 *       { label: 'Overstock', value: 10, color: '#2563EB' },
 *     ]}
 *     centerLabel="120"
 *     centerSub="Products"
 *   />
 */

export function DonutChart({
  segments = [],
  size = 120,
  strokeWidth = 18,
  centerLabel,
  centerSub,
}) {
  if (!segments.length) return null;

  const cx = size / 2;
  const cy = size / 2;
  const r = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * r;
  const total = segments.reduce((s, seg) => s + seg.value, 0);

  if (total === 0) return null;

  let cumulativePercent = 0;
  const svgSegments = segments.map((seg) => {
    const percent = seg.value / total;
    const dashArray = `${percent * circumference} ${(1 - percent) * circumference}`;
    const dashOffset = -cumulativePercent * circumference;
    cumulativePercent += percent;
    return { ...seg, dashArray, dashOffset };
  });

  return (
    <div className="inline-flex flex-col items-center gap-3">
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          style={{ transform: 'rotate(-90deg)' }}
        >
          {/* Track */}
          <circle
            cx={cx} cy={cy} r={r}
            fill="none"
            stroke="var(--border-default)"
            strokeWidth={strokeWidth}
          />
          {/* Segments */}
          {svgSegments.map((seg, i) => (
            <circle
              key={i}
              cx={cx} cy={cy} r={r}
              fill="none"
              stroke={seg.color}
              strokeWidth={strokeWidth}
              strokeDasharray={seg.dashArray}
              strokeDashoffset={seg.dashOffset}
              strokeLinecap="butt"
            />
          ))}
        </svg>
        {(centerLabel || centerSub) && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
          >
            {centerLabel && (
              <span
                className="text-[18px] font-semibold leading-none"
                style={{
                  fontVariantNumeric: 'tabular-nums',
                  fontFamily: 'Manrope, sans-serif',
                  color: 'var(--text-primary)',
                }}
              >
                {centerLabel}
              </span>
            )}
            {centerSub && (
              <span className="text-[11px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                {centerSub}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Legend */}
      {segments.length > 0 && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 justify-center">
          {segments.map((seg, i) => (
            <div key={i} className="flex items-center gap-1">
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: seg.color }}
              />
              <span className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>
                {seg.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
